/**
 * Core types for the user-navigated wizard (step-flow) engine.
 *
 * A wizard config is a pure, serializable value: an ordered list of step
 * declarations whose `enabled` / `validate` guards are referenced **by name**
 * and injected at interpretation time (see `interpretWizard` / `useWizard`) —
 * the same purity contract as the state-machine core, which is what makes the
 * config typeable, diagrammable (`wizardToMermaid`), and unit-testable.
 *
 * Unlike the state machine, a wizard carries no `context` and owns no async:
 * field state stays in react-hook-form / atoms, mutations stay in React Query
 * (or a co-located state machine), and guards/observers close over that scope.
 */

/**
 * Event types as they appear in transition records (`onTransition`). The first
 * four are dispatched through the actor's `next()` / `back()` / `skip()` /
 * `goTo()` — there is no raw `send()` on a wizard actor.
 */
export const NEXT_EVENT = 'NEXT';
export const BACK_EVENT = 'BACK';
export const SKIP_EVENT = 'SKIP';
export const GOTO_EVENT = 'GOTO';
/**
 * Engine-emitted when the current step's `enabled` guard flips false — never
 * dispatched by consumers (call `refresh()` instead; `useWizard` already does
 * after every commit). Exported so `onTransition` observers can match the
 * records it produces.
 */
export const RECONCILE_EVENT = 'RECONCILE';
/** Emitted by `actor.reset()` — the wizard returned to its initial position. */
export const RESET_EVENT = 'RESET';

export type WizardEvent<TStep extends string = string> =
  | { type: typeof NEXT_EVENT }
  | { type: typeof BACK_EVENT }
  | { type: typeof SKIP_EVENT }
  | { type: typeof GOTO_EVENT; step: TStep }
  | { type: typeof RECONCILE_EVENT };

/** One declared step. Order of declaration is the wizard's forward order. */
export type WizardStepConfig<TStep extends string = string> = {
  id: TStep;
  /**
   * Guard name — the step is part of the effective list only while the guard
   * returns true. Re-evaluated on every transition and on `refresh()`.
   */
  enabled?: string;
  /** Guard name — gates forward navigation (`next` / forward `goTo`) out of this step. */
  validate?: string;
  /** `skip()` is honored only on steps that declare this. */
  skippable?: boolean;
  /** Where `back()` lands from this step — wins over the visited-history stack. */
  backTarget?: TStep;
  /**
   * Set `false` to suppress back navigation from this step entirely:
   * `snapshot.canGoBack` reads false and `back()` is refused. Programmatic
   * backward `goTo` is unaffected — this governs the Back affordance, not
   * navigation in general. Defaults to true.
   */
  canGoBack?: boolean;
};

/**
 * Direction the engine moves when the current step's `enabled` guard flips
 * false. `forward` (default) advances to the next enabled step, completing
 * when none follows. `backward` retreats to the nearest earlier enabled step
 * (falling forward only when nothing earlier survives) — for form flows where
 * later steps depend on data the user hasn't entered yet.
 */
export type WizardReconcilePolicy = 'forward' | 'backward';

/** The value produced by `createWizard`. `TStep` is the union of step ids. */
export type Wizard<TStep extends string = string> = {
  id: string;
  steps: readonly WizardStepConfig<TStep>[];
  /** All declared step ids in order — the union backing `snapshot.step`. */
  stepIds: readonly TStep[];
  reconcile: WizardReconcilePolicy;
};

/**
 * The pure reducer state. `history` is the visited path (steps left going
 * forward) — `back()` walks it, which is what makes branching flows return to
 * where they actually came from.
 */
export type WizardState<TStep extends string = string> = {
  step: TStep;
  completed: ReadonlySet<TStep>;
  history: readonly TStep[];
  done: boolean;
};

/**
 * Guard implementations close over consumer scope (form state, atoms, flags)
 * and are read fresh on every evaluation — they receive no arguments.
 */
export type WizardGuardFn = () => boolean;

/**
 * Emitted on every taken transition. Structurally a superset of the
 * state-machine `TransitionRecord`, so `createTransitionLogger` accepts it
 * unchanged. `stepNumber` / `totalSteps` describe the post-transition snapshot.
 */
export type WizardTransitionRecord = {
  from: string;
  event: string;
  to: string;
  stepNumber: number;
  totalSteps: number;
};

/**
 * What a persistence adapter stores — everything needed to resume. JSON-safe
 * by construction: `completed` / `history` are plain arrays here, unlike the
 * Set / readonly arrays on `WizardState`. The engine converts in both
 * directions (`[...set]` on save, `new Set(...)` on restore) — adapters never
 * convert, they only round-trip this shape.
 */
export type WizardPersistedSnapshot<TStep extends string = string> = {
  step: TStep;
  completed: TStep[];
  history: TStep[];
  done: boolean;
};

/**
 * Pluggable persistence. The engine calls `save` after every taken transition
 * and `restore` exactly once on start; what/where/how (localStorage, Tauri
 * disk, backend, filtering to a subset of steps) is entirely the adapter's
 * business. `restore` may be async — the wizard exposes `isHydrating` until it
 * settles, and a restore that resolves after the user has navigated is
 * discarded.
 *
 * `save` is optional: a restore-only adapter is valid when the consumer owns
 * its own write path (e.g. the persisted document couples the step with other
 * feature data and needs a single writer).
 */
export type WizardPersistenceAdapter<TStep extends string = string> = {
  save?: (snapshot: WizardPersistedSnapshot<TStep>) => void | Promise<void>;
  restore: () =>
    | WizardPersistedSnapshot<TStep>
    | null
    | Promise<WizardPersistedSnapshot<TStep> | null>;
  clear?: () => void | Promise<void>;
};

/** The snapshot consumers read. `stepNumber` is 1-based within the effective list. */
export type WizardSnapshot<TStep extends string = string> = {
  step: TStep;
  /**
   * The effective step list behind this snapshot (declared order minus
   * disabled steps) — for consumer-side derivations like progress over a
   * subset of steps. Do not keep a filtered copy of it in feature state.
   */
  steps: readonly TStep[];
  stepNumber: number;
  totalSteps: number;
  /** 0–1 across the effective list; 1 when `done`. */
  progress: number;
  canGoBack: boolean;
  done: boolean;
  /** True while an async persistence `restore()` is pending. */
  isHydrating: boolean;
  isCompleted: (step: TStep) => boolean;
};

/** Implementations injected at interpretation time (kept out of the pure config). */
export type WizardImplementations<TStep extends string = string> = {
  guards?: Record<string, WizardGuardFn>;
  /** Fires on every taken transition — the single hook for step analytics. */
  onTransition?: (record: WizardTransitionRecord) => void;
  persistence?: WizardPersistenceAdapter<TStep>;
  /**
   * Seed the starting position (mirrors the state machine's `input`). `step`
   * must be a declared id (throws at creation otherwise) and becomes the
   * initial — and SSR — snapshot, so flows whose entry step varies by variant
   * (or a dev deep-link) start there with no wrong-step frame. Read once at
   * creation; a persistence `restore()` still overrides it.
   */
  input?: { step?: TStep };
};
